import { computed, reactive, ref } from 'vue';
import {
  buildBranchMemberOptions,
  fetchAllMembersAsUsers,
  getMemberPositionsFromUsers,
  resolveMembershipBranchIdForMapping,
  usePermittedMembers,
} from '@/entities/User';
import type { CollaboratorLike, MemberOption } from '@/entities/User';
import { formatDateForApi, parseCivilDate, stringToDate } from '@/shared/lib';
import { getRecordHomologation, updateRecordHomologation } from '../api/records.api';
import type {
  RecordHomologationPayload,
  RecordHomologationResponse,
  RecordHomologationStage,
} from './records.types';

export const RECORD_HOMOLOGATION_ELABORATION_LABEL = 'Elaboração';

/** Elaboração is absent on purpose: the record stores no cargo for the elaborator. */
export const RECORD_HOMOLOGATION_DECISION_STAGES = [
  { key: 'approval', label: 'Aprovação' },
  { key: 'verification', label: 'Verificação' },
] as const;

export type RecordHomologationDecisionKey =
  (typeof RECORD_HOMOLOGATION_DECISION_STAGES)[number]['key'];

export type RecordHomologationStageKey = 'elaboration' | RecordHomologationDecisionKey;

/**
 * The date stays in wire form so what the form diffs is what it would send. It
 * arrives as an instant and leaves as `YYYY-MM-DD`, because the picker only
 * offers a day; the endpoint keeps the São Paulo time the row already had.
 */
interface StageForm {
  memberId: number | undefined;
  date: string | undefined;
}

interface DecisionStageForm extends StageForm {
  positionId: number | undefined;
}

interface RecordedNames {
  memberName: string | null;
  positionName: string | null;
}

const buildStageForm = (): StageForm => ({ memberId: undefined, date: undefined });

const buildDecisionStageForm = (): DecisionStageForm => ({
  memberId: undefined,
  positionId: undefined,
  date: undefined,
});

const SP_CIVIL_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Comparing vigência against a verification instant has to happen in São Paulo,
 * not in the reader's timezone, or the same record would validate differently
 * depending on where it is opened. `en-CA` renders `YYYY-MM-DD`, which turns the
 * comparison into a string comparison.
 */
const toSaoPauloCivilDay = (value: string): string | null => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return SP_CIVIL_DAY_FORMATTER.format(parsed);
};

/**
 * Form of the "Editar Homologação" modal of a record. Every person here is a
 * member, never a user: what a decision records is the member, because a member
 * belongs to a branch and a user does not.
 */
export function useRecordHomologation() {
  const branchId = resolveMembershipBranchIdForMapping(undefined);

  const isLoading = ref(false);
  const isSaving = ref(false);
  const error = ref<string | null>(null);

  const header = reactive<{ code: string; title: string | null; revision: number | null }>({
    code: '',
    title: null,
    revision: null,
  });

  const stages = reactive<{
    elaboration: StageForm;
    approval: DecisionStageForm;
    verification: DecisionStageForm;
  }>({
    elaboration: buildStageForm(),
    approval: buildDecisionStageForm(),
    verification: buildDecisionStageForm(),
  });

  /** Civil date, `YYYY-MM-DD`. */
  const effectiveDate = ref<string | undefined>(undefined);

  const previousRevisionEffectiveDate = ref<string | null>(null);
  /** Elaboração is never missing: a revision always has a creator to fall back to. */
  const missingStages = reactive<Record<RecordHomologationDecisionKey, boolean>>({
    approval: false,
    verification: false,
  });
  const recordedNames = reactive<Record<RecordHomologationStageKey, RecordedNames>>({
    elaboration: { memberName: null, positionName: null },
    approval: { memberName: null, positionName: null },
    verification: { memberName: null, positionName: null },
  });

  let baseline = '';

  /**
   * Inactive members included: one who lost the permission is still the
   * historical fact of the record, and the filtered pools leave them out.
   */
  const branchMembers = ref<CollaboratorLike[]>([]);

  const elaborationPool = usePermittedMembers({
    branchId,
    requiredPermissions: [{ action: 'manage', subject: 'RecordManagement' }],
  });
  const approvalPool = usePermittedMembers({
    branchId,
    requiredPermissions: [{ action: 'manageApproval', subject: 'Homologation' }],
  });
  const verificationPool = usePermittedMembers({
    branchId,
    requiredPermissions: [{ action: 'manageVerification', subject: 'Homologation' }],
  });

  const pools = {
    elaboration: elaborationPool,
    approval: approvalPool,
    verification: verificationPool,
  } as const;

  const snapshot = () =>
    JSON.stringify({
      stages: {
        elaboration: { ...stages.elaboration },
        approval: { ...stages.approval },
        verification: { ...stages.verification },
      },
      effectiveDate: effectiveDate.value,
    });

  const applyDecisionStage = (
    key: RecordHomologationDecisionKey,
    stage: RecordHomologationStage | null
  ): void => {
    missingStages[key] = stage === null;
    stages[key].memberId = stage?.memberId ?? undefined;
    stages[key].positionId = stage?.positionId ?? undefined;
    stages[key].date = stage?.date ?? undefined;
    recordedNames[key].memberName = stage?.memberName ?? null;
    recordedNames[key].positionName = stage?.positionName ?? null;
  };

  const applyResponse = (response: RecordHomologationResponse): void => {
    header.code = response.code;
    header.title = response.title;
    header.revision = response.revision;

    stages.elaboration.memberId = response.elaboration.memberId ?? undefined;
    stages.elaboration.date = response.elaboration.date ?? undefined;
    recordedNames.elaboration.memberName = response.elaboration.memberName;

    applyDecisionStage('approval', response.approval);
    applyDecisionStage('verification', response.verification);

    effectiveDate.value = response.effectiveDate ?? undefined;
    previousRevisionEffectiveDate.value = response.previousRevisionEffectiveDate;

    // Last, or every field just written would read as a change.
    baseline = snapshot();
  };

  const load = async (matrixId: number): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const [response] = await Promise.all([
        getRecordHomologation(matrixId),
        loadBranchMembers(),
        elaborationPool.fetchUsers(),
        approvalPool.fetchUsers(),
        verificationPool.fetchUsers(),
      ]);

      applyResponse(response);
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const loadBranchMembers = async (): Promise<void> => {
    if (branchId === undefined) return;

    try {
      branchMembers.value = await fetchAllMembersAsUsers(branchId, undefined, {
        includeInactiveMembers: true,
      });
    } catch {
      // A missing pool only costs the name of a member who is no longer eligible,
      // so the form still opens with the ids the endpoint returned.
      branchMembers.value = [];
    }
  };

  /** Plus the recorded member when it is no longer permitted, so it does not vanish. */
  const memberOptions = (key: RecordHomologationStageKey): MemberOption[] => {
    const options = [...pools[key].userOptions.value];
    const recordedId = stages[key].memberId;

    if (recordedId !== undefined && !options.some((option) => option.id === recordedId)) {
      options.push({
        id: recordedId,
        name: recordedNames[key].memberName ?? resolveMemberName(recordedId) ?? '',
        registrationNumber: null,
      });
    }

    return options;
  };

  const resolveMemberName = (memberId: number): string | undefined =>
    buildBranchMemberOptions(branchMembers.value, branchId).find((option) => option.id === memberId)
      ?.name;

  /**
   * Cargos of the member picked on the decision, plus the recorded one when it is
   * no longer among them. The pool merges the filtered one with every branch
   * member, because a member who lost the permission still has cargos to show.
   */
  const positionOptions = (
    key: RecordHomologationDecisionKey
  ): Array<{ id: number; name: string }> => {
    const memberId = stages[key].memberId;
    if (memberId === undefined) return [];

    const pool = [...(pools[key].users.value.data ?? []), ...branchMembers.value];
    const options = getMemberPositionsFromUsers(memberId, pool, branchId);
    const recordedId = stages[key].positionId;

    if (recordedId !== undefined && !options.some((option) => option.id === recordedId)) {
      return [...options, { id: recordedId, name: recordedNames[key].positionName ?? '' }];
    }

    return options;
  };

  /** Changing the responsável drops the cargo: it belonged to the previous person. */
  const setMemberId = (key: RecordHomologationStageKey, memberId: number | undefined): void => {
    if (memberId === stages[key].memberId) return;

    stages[key].memberId = memberId;

    if (key !== 'elaboration') {
      stages[key].positionId = undefined;
      recordedNames[key].positionName = null;
    }
  };

  const setPositionId = (
    key: RecordHomologationDecisionKey,
    positionId: number | undefined
  ): void => {
    stages[key].positionId = positionId;
  };

  const stageDate = (key: RecordHomologationStageKey): Date | null =>
    stages[key].date ? stringToDate(stages[key].date) : null;

  const setStageDate = (key: RecordHomologationStageKey, date: Date | null): void => {
    stages[key].date = date ? (formatDateForApi(date) ?? undefined) : undefined;
  };

  const effectiveDateAsDate = computed<Date | null>(() =>
    effectiveDate.value ? parseCivilDate(effectiveDate.value) : null
  );

  const setEffectiveDate = (date: Date | null): void => {
    effectiveDate.value = date ? (formatDateForApi(date) ?? undefined) : undefined;
  };

  /** A past date is deliberately allowed: this is where a wrong date gets corrected. */
  const effectiveDateError = computed<string | null>(() => {
    if (!effectiveDate.value) return null;

    const effectiveDay = toSaoPauloCivilDay(effectiveDate.value);
    if (!effectiveDay) return null;

    const previous = previousRevisionEffectiveDate.value;
    if (previous) {
      const previousDay = toSaoPauloCivilDay(previous);
      if (previousDay && effectiveDay <= previousDay) {
        return 'A data de vigência deve ser posterior à vigência da revisão anterior';
      }
    }

    const verifiedAt = stages.verification.date;
    if (verifiedAt) {
      const verificationDay = toSaoPauloCivilDay(verifiedAt);
      if (verificationDay && effectiveDay < verificationDay) {
        return 'A data de vigência não pode ser anterior à data de verificação';
      }
    }

    return null;
  });

  /** A decision with a responsável needs a cargo, or the API rejects the patch. */
  const arePositionsValid = computed(() =>
    RECORD_HOMOLOGATION_DECISION_STAGES.every(({ key }) => {
      const stage = stages[key];
      return stage.memberId === undefined || stage.positionId !== undefined;
    })
  );

  const hasChanges = computed(() => snapshot() !== baseline);

  const isFormValid = computed(() => arePositionsValid.value && effectiveDateError.value === null);

  const canSave = computed(
    () => hasChanges.value && isFormValid.value && !isLoading.value && !isSaving.value
  );

  const assignChanged = <K extends keyof RecordHomologationPayload>(
    payload: RecordHomologationPayload,
    key: K,
    current: RecordHomologationPayload[K],
    before: RecordHomologationPayload[K]
  ): void => {
    if (current === undefined || current === before) return;
    payload[key] = current;
  };

  /** Only what changed, so an untouched approval is never rewritten. */
  const buildPayload = (): RecordHomologationPayload => {
    const previous = JSON.parse(baseline) as {
      stages: {
        elaboration: StageForm;
        approval: DecisionStageForm;
        verification: DecisionStageForm;
      };
      effectiveDate: string | undefined;
    };
    const payload: RecordHomologationPayload = {};

    assignChanged(
      payload,
      'elaborationMemberId',
      stages.elaboration.memberId,
      previous.stages.elaboration.memberId
    );
    assignChanged(
      payload,
      'elaboratedAt',
      stages.elaboration.date,
      previous.stages.elaboration.date
    );

    const decisionKeys = {
      approval: {
        member: 'approvalMemberId',
        position: 'approvalPositionId',
        date: 'approvedAt',
      },
      verification: {
        member: 'verificationMemberId',
        position: 'verificationPositionId',
        date: 'verifiedAt',
      },
    } as const;

    for (const { key } of RECORD_HOMOLOGATION_DECISION_STAGES) {
      const current = stages[key];
      const before = previous.stages[key];
      const names = decisionKeys[key];

      assignChanged(payload, names.member, current.memberId, before.memberId);
      assignChanged(payload, names.position, current.positionId, before.positionId);
      assignChanged(payload, names.date, current.date, before.date);
    }

    assignChanged(payload, 'effectiveDate', effectiveDate.value, previous.effectiveDate);

    return payload;
  };

  const save = async (matrixId: number): Promise<void> => {
    isSaving.value = true;
    error.value = null;

    try {
      applyResponse(await updateRecordHomologation(matrixId, buildPayload()));
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    header,
    stages,
    missingStages,
    effectiveDate,
    effectiveDateAsDate,
    previousRevisionEffectiveDate,
    isLoading,
    isSaving,
    error,
    load,
    save,
    memberOptions,
    positionOptions,
    setMemberId,
    setPositionId,
    stageDate,
    setStageDate,
    setEffectiveDate,
    effectiveDateError,
    hasChanges,
    isFormValid,
    canSave,
    buildPayload,
  };
}

/** The API message when there is one, so a 400 explains itself. */
function extractErrorMessage(err: unknown): string {
  const response = (err as { response?: { data?: { message?: unknown; errors?: unknown } } })
    ?.response;
  const message = response?.data?.message;

  if (typeof message === 'string' && message.trim().length > 0) return message;
  if (Array.isArray(response?.data?.errors)) {
    const first = response.data.errors[0] as { message?: unknown } | undefined;
    if (typeof first?.message === 'string') return first.message;
  }

  return 'Erro ao salvar registro. Tente novamente';
}
